import React from 'react';
import { Button, Modal } from '@brighterion/br-ui';
import { useTranslation } from 'react-i18next';
import useAddNewListEntry from 'Hooks/query-hooks/listEntry/useAddNewListEntry';
import useAlertBanner from 'Hooks/reduxHelperHooks/useAlertBanner';
import { LIST_TYPES } from 'Common/enums';
import AddListEntryForm from './AddListEntryForm';
import styles from './AddListEntry.css';

type Props = {
    listId: number;
    listType: SingleListType;
    listName: string;
    merchantSpecific: boolean;
    customListType?: string;
    isMissingConfigCustomList?: boolean;
};

const AddListEntry = ({
    listType,
    listId,
    listName,
    merchantSpecific,
    customListType,
    isMissingConfigCustomList
}: Props) => {
    const numOfConsecutiveUpdates = React.useRef<number>(0);
    const [isOpen, setIsOpen] = React.useState(false);
    const { t } = useTranslation();
    const { addAlert } = useAlertBanner();
    const mutation = useAddNewListEntry({
        listId,
        mutationOptions: {
            onSuccess: () => {
                numOfConsecutiveUpdates.current++;
            }
        }
    });

    const onClose = () => {
        setIsOpen(false);
        // clear the error or data of a mutation request.
        mutation.reset();
        if (numOfConsecutiveUpdates.current > 0) {
            addAlert({
                status: 'success',
                id: 'new-entry-added',
                content:
                    listType === LIST_TYPES.RANGE
                        ? t('addListEntry.addedRangeEntry', { listName })
                        : t('addListEntry.addedEntry', { count: numOfConsecutiveUpdates.current, listName })
            });
            numOfConsecutiveUpdates.current = 0;
        }
    };

    return (
        <>
            <Button
                disabled={isMissingConfigCustomList}
                onClick={() => setIsOpen(true)}
                size='small'
                dataTestId='add-entry-button'
            >
                {t('Add Entry')}
            </Button>
            <Modal
                dataTestId='add-list-entry-modal'
                isSubmitting={mutation.isLoading}
                isOpen={isOpen}
                closeOnClickOutside={false}
                closeModal={onClose}
                containerClass={styles.addListEntryModal}
            >
                <Modal.Title
                    isSubmitting={mutation.isLoading}
                    id='add-list-entry-modal-title'
                    title={t('Add Entry To List')}
                    onClose={onClose}
                />
                <AddListEntryForm
                    formikKey={numOfConsecutiveUpdates.current}
                    handleSubmit={(values) =>
                        mutation.mutate(values, {
                            onSuccess: onClose
                        })
                    }
                    handleSaveAndContinute={mutation.mutate}
                    isLoading={mutation.isLoading}
                    error={mutation.error}
                    listType={listType}
                    customListType={customListType}
                    merchantSpecific={merchantSpecific}
                    onClose={onClose}
                />
            </Modal>
        </>
    );
};

export default AddListEntry;
